import type { Subscription, SubscriptionInput } from "../types/subscription";
import {
  fetchMe,
  fetchTrackerSubscriptionsCloud,
  putTrackerSubscriptionsCloud,
} from "./api/client";
import { clearUserSettingsLocalCache } from "./user-settings";
import { isApiConfigured } from "./api/config";
import { isCloudSessionActive, setCloudSessionActive } from "./cloud-session";

export const DB_URL = "sqlite:subscriptions.db";

const BROWSER_STORAGE_KEY = "dripwatch-subscriptions-v1";
/** Set after we attempt one-time copy from legacy SQLite (desktop only). */
const SQLITE_MIGRATION_KEY = "dripwatch-sqlite-migration-v1";

function isTauriRuntime(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as unknown as { __TAURI_INTERNALS__?: { invoke?: unknown } })
      .__TAURI_INTERNALS__?.invoke === "function"
  );
}

function readBrowserStore(): Subscription[] {
  try {
    const raw = localStorage.getItem(BROWSER_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as Subscription[];
  } catch {
    return [];
  }
}

function writeBrowserStore(rows: Subscription[]): void {
  localStorage.setItem(BROWSER_STORAGE_KEY, JSON.stringify(rows));
}

/** Drop the in-browser list (empty guest state). Used on sign-out so cloud data is not left on device. */
export function clearSubscriptionsLocalCache(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(BROWSER_STORAGE_KEY);
}

const LOGOUT_GUEST_MARKER = "dripwatch-logout-guest-v1";

/**
 * Call when the user chooses Log out. Auth0 redirects with a full page load, so React refs
 * cannot detect authenticated → guest; we must clear storage before navigation.
 */
export function resetLocalSubscriptionsForAuthLogout(): void {
  clearSubscriptionsLocalCache();
  clearUserSettingsLocalCache();
  setCloudSessionActive(false);
  try {
    sessionStorage.setItem(LOGOUT_GUEST_MARKER, "1");
  } catch {
    /* private / blocked storage */
  }
}

/** After redirect back from Auth0 logout; idempotent safety clear. */
export function consumeLogoutGuestMarker(): void {
  try {
    if (sessionStorage.getItem(LOGOUT_GUEST_MARKER) !== "1") return;
    sessionStorage.removeItem(LOGOUT_GUEST_MARKER);
    clearSubscriptionsLocalCache();
    clearUserSettingsLocalCache();
    setCloudSessionActive(false);
  } catch {
    /* ignore */
  }
}

function sortSubscriptions(rows: Subscription[]): Subscription[] {
  return [...rows].sort((a, b) => {
    if (b.active !== a.active) return b.active - a.active;
    return a.next_renewal_date.localeCompare(b.next_renewal_date);
  });
}

/**
 * Desktop builds used SQLite; the app now uses the same localStorage store as the web app.
 * Run once: if storage is empty, copy rows from legacy SQLite, then mark complete.
 */
export async function initSubscriptionStore(): Promise<void> {
  if (!isTauriRuntime()) return;
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(SQLITE_MIGRATION_KEY)) return;

  const existing = readBrowserStore();
  if (existing.length > 0) {
    localStorage.setItem(SQLITE_MIGRATION_KEY, "1");
    return;
  }

  try {
    const { default: Database } = await import("@tauri-apps/plugin-sql");
    const db = await Database.load(DB_URL);
    const rows = await db.select<Subscription[]>(
      "SELECT id, name, amount, currency, billing_cycle, custom_period_days, category, next_renewal_date, notes, active, created_at FROM subscriptions ORDER BY active DESC, next_renewal_date ASC",
    );
    if (rows.length > 0) {
      writeBrowserStore(sortSubscriptions(rows));
    }
  } catch {
    /* no legacy DB or plugin unavailable */
  } finally {
    localStorage.setItem(SQLITE_MIGRATION_KEY, "1");
  }
}

export type CloudLinkAuth = {
  isAuthenticated: boolean;
  isLoading: boolean;
  ensureApiSession: () => Promise<void>;
};

/** Sorted list from local cache (after sync, this matches cloud when linked). */
export function getSubscriptionsSnapshot(): Subscription[] {
  return sortSubscriptions(readBrowserStore());
}

/**
 * Logged out: browser-only. Logged in with API cookie: merge guest upload if account is empty, else load cloud into cache.
 */
export async function syncSubscriptionsWithCloudIfLinked(
  auth: CloudLinkAuth,
): Promise<void> {
  if (!isApiConfigured()) {
    setCloudSessionActive(false);
    return;
  }
  if (!auth.isAuthenticated || auth.isLoading) {
    setCloudSessionActive(false);
    return;
  }
  await auth.ensureApiSession();
  const me = await fetchMe();
  if (!me) {
    setCloudSessionActive(false);
    return;
  }

  const local = readBrowserStore();
  let remote: Subscription[];
  try {
    remote = await fetchTrackerSubscriptionsCloud();
  } catch {
    setCloudSessionActive(false);
    return;
  }

  setCloudSessionActive(true);
  // Remote wins once the account has any saved rows; otherwise upload guest data.
  if (remote.length === 0 && local.length > 0) {
    try {
      await putTrackerSubscriptionsCloud(local);
    } catch {
      setCloudSessionActive(false);
      return;
    }
  } else if (remote.length > 0) {
    writeBrowserStore(sortSubscriptions(remote));
  }
}

async function mirrorTrackerToCloudIfLinked(): Promise<void> {
  if (!isCloudSessionActive() || !isApiConfigured()) return;
  await putTrackerSubscriptionsCloud(readBrowserStore());
}

export async function fetchSubscriptions(): Promise<Subscription[]> {
  if (isCloudSessionActive() && isApiConfigured()) {
    try {
      const rows = await fetchTrackerSubscriptionsCloud();
      writeBrowserStore(sortSubscriptions(rows));
      return sortSubscriptions(rows);
    } catch {
      return sortSubscriptions(readBrowserStore());
    }
  }
  return sortSubscriptions(readBrowserStore());
}

export async function insertSubscription(input: SubscriptionInput): Promise<void> {
  const rows = readBrowserStore();
  const nextId = rows.reduce((m, r) => Math.max(m, r.id), 0) + 1;
  const created = new Date().toISOString();
  rows.push({
    id: nextId,
    name: input.name.trim(),
    amount: input.amount,
    currency: input.currency || "USD",
    billing_cycle: input.billing_cycle,
    custom_period_days: input.billing_cycle === "custom_days" ? input.custom_period_days : null,
    category: input.category.trim() || "General",
    next_renewal_date: input.next_renewal_date,
    notes: input.notes?.trim() || null,
    active: input.active ? 1 : 0,
    created_at: created,
  });
  writeBrowserStore(rows);
  try {
    await mirrorTrackerToCloudIfLinked();
  } catch (e) {
    console.warn("Could not sync to your account", e);
  }
}

export async function updateSubscription(
  id: number,
  input: SubscriptionInput,
): Promise<void> {
  const rows = readBrowserStore();
  const i = rows.findIndex((r) => r.id === id);
  if (i === -1) return;
  const prev = rows[i]!;
  rows[i] = {
    ...prev,
    name: input.name.trim(),
    amount: input.amount,
    currency: input.currency || "USD",
    billing_cycle: input.billing_cycle,
    custom_period_days: input.billing_cycle === "custom_days" ? input.custom_period_days : null,
    category: input.category.trim() || "General",
    next_renewal_date: input.next_renewal_date,
    notes: input.notes?.trim() || null,
    active: input.active ? 1 : 0,
  };
  writeBrowserStore(rows);
  try {
    await mirrorTrackerToCloudIfLinked();
  } catch (e) {
    console.warn("Could not sync to your account", e);
  }
}

export async function setSubscriptionActive(id: number, active: boolean): Promise<void> {
  const rows = readBrowserStore();
  const i = rows.findIndex((r) => r.id === id);
  if (i === -1) return;
  rows[i] = { ...rows[i]!, active: active ? 1 : 0 };
  writeBrowserStore(rows);
  try {
    await mirrorTrackerToCloudIfLinked();
  } catch (e) {
    console.warn("Could not sync to your account", e);
  }
}

export async function deleteSubscription(id: number): Promise<void> {
  writeBrowserStore(readBrowserStore().filter((r) => r.id !== id));
  try {
    await mirrorTrackerToCloudIfLinked();
  } catch (e) {
    console.warn("Could not sync to your account", e);
  }
}
